var _excluded = ["open", "duration", "variant", "hideIcon", "message", "actionLabel", "onAction", "onClose", "hideCloseButton", "theme"];

var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5, _templateObject6;

function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

function _taggedTemplateLiteralLoose(strings, raw) { if (!raw) { raw = strings.slice(0); } strings.raw = raw; return strings; }

import React, { memo, useEffect, useRef } from 'react';
import styled, { keyframes, withTheme } from 'styled-components';
import { func, string, bool, number, oneOf } from 'prop-types';
import { media } from '@gympass/yoga-helpers';
import { Close } from '@gympass/yoga-icons';
import { theme } from '../../Theme';
import Box from '../../Box';
import Button from '../../Button';
import Icon from '../../Icon';
import Text from '../../Text';
var IconButtonWrapper = styled.div(_templateObject || (_templateObject = _taggedTemplateLiteralLoose(["\n  display: flex;\n  align-items: center;\n\n  cursor: pointer;\n\n  &:hover {\n    svg {\n      fill: ", ";\n    }\n  }\n"])), theme.colors.text.secondary);
var ActionsWrapper = styled.aside(_templateObject2 || (_templateObject2 = _taggedTemplateLiteralLoose(["\n  display: flex;\n  align-items: center;\n\n  svg {\n    margin-left: ", "px;\n    margin-right: ", ";\n  }\n"])), theme.spacing.small, theme.spacing.zero);
var StyledSnackbar = styled.div(_templateObject3 || (_templateObject3 = _taggedTemplateLiteralLoose(["\n  ", "\n\n  ", "\n"])), function (_ref) {
  var variant = _ref.variant,
      snackbar = _ref.theme.yoga.components.snackbar;
  return "\n      display: flex;\n      align-items: center;\n      justify-content: space-between;\n      box-sizing: border-box;\n  \n      position: fixed;\n      bottom: " + snackbar.position.mobile.bottom + "px;\n      right: " + snackbar.position.mobile.right + "px;\n      left: " + snackbar.position.mobile.left + "px;\n  \n      min-width: " + snackbar.minWidth.mobile + "px;\n      max-width: " + snackbar.maxWidth["default"] + "px;\n  \n      min-height: " + snackbar.height.min + "px;\n      max-height: " + snackbar.height.max + "px;\n  \n      padding: " + snackbar.padding["default"] + "px;\n      \n      border-radius: " + snackbar.border.radius + "px;\n  \n      box-shadow: " + snackbar.shadow["default"] + ";\n\n      background-color: " + snackbar.variant.color[variant] + ";\n\n      z-index: 100;\n    ";
}, media.md(_templateObject4 || (_templateObject4 = _taggedTemplateLiteralLoose(["\n    left: auto;\n    bottom: ", "px;\n    right: ", "px;\n\n    min-width: ", "px;\n  "])), theme.components.snackbar.position.desktop.bottom, theme.components.snackbar.position.desktop.right, theme.components.snackbar.minWidth.desktop));
var fadeIn = keyframes(_templateObject5 || (_templateObject5 = _taggedTemplateLiteralLoose(["\n  0% {\n    opacity: 0;\n  }\n  100% {\n    opacity: 1;\n  }\n"])));
var AnimatedSnackbar = styled(StyledSnackbar)(_templateObject6 || (_templateObject6 = _taggedTemplateLiteralLoose(["\n  animation: ", " 0.2s ease-in-out;\n"])), fadeIn);
var Snackbar = /*#__PURE__*/React.forwardRef(function (_ref2, ref) {
  var open = _ref2.open,
      duration = _ref2.duration,
      variant = _ref2.variant,
      hideIcon = _ref2.hideIcon,
      message = _ref2.message,
      actionLabel = _ref2.actionLabel,
      onAction = _ref2.onAction,
      onClose = _ref2.onClose,
      hideCloseButton = _ref2.hideCloseButton,
      snackbar = _ref2.theme.yoga.components.snackbar,
      props = _objectWithoutPropertiesLoose(_ref2, _excluded);

  var timeoutRef = useRef();
  useEffect(function () {
    var shouldCloseOnTimer = open && duration && onClose;

    if (shouldCloseOnTimer) {
      timeoutRef.current = setTimeout(function () {
        onClose();
      }, duration);
    }

    return function () {
      return clearTimeout(timeoutRef.current);
    };
  }, [open, duration]);
  return open && /*#__PURE__*/React.createElement(AnimatedSnackbar, _extends({
    role: "alert",
    "aria-label": variant,
    variant: variant,
    ref: ref
  }, props), !hideIcon && /*#__PURE__*/React.createElement(Box, {
    display: "flex",
    alignItems: "center",
    mr: "small",
    role: "img"
  }, /*#__PURE__*/React.createElement(Icon, {
    as: snackbar.variant.icon[variant],
    fill: "secondary",
    width: "large",
    height: "large"
  })), /*#__PURE__*/React.createElement(Text.Small, {
    flex: 1,
    mr: "small",
    numberOfLines: 2
  }, message), /*#__PURE__*/React.createElement(ActionsWrapper, null, onAction && actionLabel && /*#__PURE__*/React.createElement(Button.Link, {
    onClick: onAction,
    secondary: true,
    small: true
  }, actionLabel), !hideCloseButton && onClose && /*#__PURE__*/React.createElement(IconButtonWrapper, {
    role: "button",
    onClick: onClose
  }, /*#__PURE__*/React.createElement(Icon, {
    as: Close,
    fill: "secondary",
    size: "medium"
  }))));
});
Snackbar.propTypes = {
  /** Controls the snackbar visibility. */
  open: bool,

  /** The message shown when snackbar is opened. */
  message: string.isRequired,

  /** Function to close the snackbar. */
  onClose: func.isRequired,

  /** A number in milliseconds to close snackbar automaticaly. */
  duration: number,

  /** Label for a custom action. */
  actionLabel: string,

  /** Controls the snackbar icon visibility. */
  hideIcon: bool,

  /** Function for the custom action. The `actionLabel` becomes required when passing this function. */
  onAction: func,

  /** The style variant, it may be "success", "failure" or "info". */
  variant: oneOf(['success', 'failure', 'info']),

  /** Hides the close button. */
  hideCloseButton: bool
};
Snackbar.defaultProps = {
  open: false,
  duration: undefined,
  actionLabel: undefined,
  hideIcon: false,
  onAction: undefined,
  variant: 'success',
  hideCloseButton: false
};
export default /*#__PURE__*/memo(withTheme(Snackbar));